import PasswordRequirement from "components/PasswordRequirement";
import { routes } from "pages/routes";
import { ChangeEvent, FocusEvent, FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PasswordInput from "components/ui/PasswordInput";
import TalaeraSpinner from "../../components/TalaeraSpinner/TalaeraSpinner";
import FrameBack from "../../images/frame_back.svg?react";
import { verifyPassword } from "../../services/users/auth";
import { getErrorComponents } from "../../services/util";
import CheckCircle from "./../../images/check-circle-green.svg?react";
import MainLogo from "./../../images/main-logo.svg?react";
import Button from "components/ui/Button";
import styles from "./PasswordReset.module.scss";
import useSignInForm from "./utils";

const PasswordReset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    account,
    setAccount,
    errors: formErrors,
    touched,
    passwordStatus,
    setTouched,
    onValidate,
    onTouched,
  } = useSignInForm({ password: "", confirmPassword: "" });
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const parsed = new URLSearchParams(location.search);

  const handleChangeField = (e: ChangeEvent<HTMLInputElement>) => {
    setAccount({ ...account, [e.target.name]: e.target.value });
  };

  const handleBlurField = (e: FocusEvent<HTMLInputElement>) => {
    onTouched(e.target.name);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const isValidated = onValidate();
    setTouched({
      email: true,
      password: true,
    });

    if (!isValidated) {
      return;
    }

    setErrors("");
    setLoading(true);

    try {
      await verifyPassword({
        token: parsed.get("token")!,
        passwd: account.password,
      });
      setResetDone(true);
    } catch (error: any) {
      setErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location?.state?.error) {
      setErrors(location?.state?.error);
      navigate(location.pathname, { state: null, replace: true });
    }
    // eslint-disable-next-line
  }, [location]);

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <MainLogo />

        {errors && <div className={styles.error}>{getErrorComponents(errors)}</div>}

        {!resetDone && (
          <>
            <div className={styles.header}>
              <h1>{t("Auth.resetForm.title")}</h1>
            </div>

            <p className={styles.description}>
              {t("Auth.passwordReset.enterNewPassword")}
            </p>

            <form onSubmit={handleSubmit}>
              <div className={styles.formWrapper}>
                <PasswordInput
                  label={t("Auth.emailSignup.passwordLabel")}
                  error={touched.password && formErrors.password}
                  size="large"
                  name="password"
                  placeholder={t("Auth.resetForm.passwordPlaceholder")}
                  value={account.password}
                  onChange={handleChangeField}
                  invalid={!!(formErrors.password && touched.password)}
                  onBlur={handleBlurField}
                />
                <PasswordInput
                  label={t("Auth.resetForm.confirmPasswordLabel")}
                  error={touched.confirmPassword && formErrors.confirmPassword}
                  size="large"
                  name="confirmPassword"
                  placeholder={t("Auth.resetForm.passwordPlaceholder")}
                  value={account.confirmPassword}
                  onChange={handleChangeField}
                  invalid={!!(formErrors.confirmPassword && touched.confirmPassword)}
                  onBlur={handleBlurField}
                />

                {account.password && (
                  <div className={styles.passwordRequirement}>
                    <PasswordRequirement passwordStatus={passwordStatus} />
                  </div>
                )}

                {loading ? (
                  <div className={styles.spinner}>
                    <TalaeraSpinner />
                  </div>
                ) : (
                  <button className={styles.button} type="submit">
                    {t("Auth.passwordReset.submit")}
                  </button>
                )}
              </div>
            </form>
          </>
        )}

        {resetDone && (
          <div className={styles.resetDoneContainer}>
            <CheckCircle />

            <h1 className={styles.resetTitle}>
              {t("Auth.passwordReset.changedHeadline")}
            </h1>

            <p className={styles.resetMessage}>
              {t("Auth.passwordReset.changedMessage")}
            </p>

            <Button
              size="large"
              className={styles.loginButton}
              onClick={() =>
                navigate(routes.public.signIn.path(), { state: { resetDone: true } })
              }
            >
              {t("Auth.passwordReset.logBackIn")}
            </Button>
          </div>
        )}
      </div>

      {!resetDone && (
        <div>
          <Link className={styles.link} to={routes.public.signIn.path()}>
            ← {t("Auth.actions.backToLogin")}
          </Link>
        </div>
      )}

      <FrameBack className={styles.frameBack} />
    </div>
  );
};

export default PasswordReset;
